import React, { useState } from 'react';
import { SUBSCRIPTION_TIERS, SubscriptionTier, isComingSoon } from '../config/subscriptions';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionPlansProps {
  onSelectPlan: (tier: SubscriptionTier) => void;
  selectedTier?: string;
  showTitle?: boolean;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ 
  onSelectPlan, 
  selectedTier, 
  showTitle = true 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);

  const handleSelectPlan = async (tier: SubscriptionTier) => {
    if (isComingSoon(tier.id)) {
      // Handle coming soon - maybe show email signup modal
      alert('🚀 Coming Soon! We\'ll notify you when the learning platform is ready. For now, enjoy the games!');
      return;
    }
    
    setLoading(tier.id);
    try {
      onSelectPlan(tier);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {showTitle && (
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-200 mb-2">
            Full access to all EightyThreeLabs features
          </p>
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {SUBSCRIPTION_TIERS.map((tier) => (
          <div
            key={tier.id}
            className={`relative rounded-lg border-2 p-8 ${
              tier.popular
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            } ${
              selectedTier === tier.id
                ? 'ring-2 ring-blue-500'
                : ''
            }`}
          >
            {tier.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
            )}

            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {tier.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-200 mb-4">
                {tier.description}
              </p>
              <div className="mb-6">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                    {tier.priceDisplay}
                  </span>
                </div>
                <span className="text-gray-600 dark:text-gray-200">
                  /{tier.interval}
                </span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {tier.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-200">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectPlan(tier)}
              disabled={loading === tier.id}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                isComingSoon(tier.id)
                  ? 'bg-gray-400 hover:bg-gray-500 text-white cursor-pointer'
                  : tier.popular
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-700 dark:hover:bg-gray-600'
              } ${
                selectedTier === tier.id
                  ? 'ring-2 ring-offset-2 ring-blue-500'
                  : ''
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading === tier.id ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : isComingSoon(tier.id) ? (
                '🚀 Notify Me When Ready'
              ) : selectedTier === tier.id ? (
                'Selected'
              ) : (
                'Select Plan'
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">
            🚀 What You Get
          </h3>
          <p className="text-blue-700 dark:text-blue-300 text-sm">
            Full access to all mathematical challenges, progress tracking, and premium features for just $29.99/year.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;